import { useMemo } from 'react'
import { festivalWindow } from '@shared/dates'
import type { SearchArtist, SearchEvent, SearchSet } from '@shared/searchIndex'
import { isUpcoming } from '../lib/searchRanking'
import { useSearchIndex } from './useSearchIndex'
import { useNow } from './useNow'
import { useOnlineStatus } from './useOnlineStatus'

export interface ArtistEventAppearance {
  event: SearchEvent
  /** The artist's sets at this event by day and time; empty when only the lineup is out. */
  sets: SearchSet[]
}

const endOf = (ev: SearchEvent) => festivalWindow(ev).endDate ?? ev.startDate

/**
 * Every published event an artist plays, split into upcoming (soonest first) and
 * past (latest first), read off the global search index — no Firestore reads, and
 * it works offline once the index is cached. b2b sets count for each member.
 * Cancelled events are dropped: an artist page is discovery, not your plans.
 */
export function useArtistAppearances(artistId: string | undefined): {
  artist: SearchArtist | undefined
  upcoming: ArtistEventAppearance[]
  past: ArtistEventAppearance[]
  /** A named collab's members, and the collabs this artist is a member of. */
  members: SearchArtist[]
  collabs: SearchArtist[]
  loading: boolean
} {
  const { index, loading } = useSearchIndex()
  const now = useNow()
  const online = useOnlineStatus()

  const artist = useMemo(
    () => (index && artistId ? index.artists.find(a => a.id === artistId) : undefined),
    [index, artistId],
  )

  const { upcoming, past } = useMemo(() => {
    const upcoming: ArtistEventAppearance[] = []
    const past: ArtistEventAppearance[] = []
    if (!index || !artist) return { upcoming, past }

    const setsByEvent = new Map<number, SearchSet[]>()
    for (const si of artist.setIdx) {
      const set = index.sets[si]
      const list = setsByEvent.get(set.eventIdx) ?? []
      list.push(set)
      setsByEvent.set(set.eventIdx, list)
    }

    for (const ei of artist.eventIdx) {
      const event = index.events[ei]
      if (!event || event.cancelled) continue
      const sets = (setsByEvent.get(ei) ?? [])
        .sort((a, b) => a.day.localeCompare(b.day) || a.startTime.localeCompare(b.startTime))
      ;(isUpcoming(event, now) ? upcoming : past).push({ event, sets })
    }
    upcoming.sort((a, b) => a.event.startDate.localeCompare(b.event.startDate))
    past.sort((a, b) => endOf(b.event).localeCompare(endOf(a.event)))
    return { upcoming, past }
  }, [index, artist, now])

  const { members, collabs } = useMemo(() => {
    const resolve = (idx: number[] | undefined) =>
      (idx ?? []).map(i => index?.artists[i]).filter((a): a is SearchArtist => !!a)
    return { members: resolve(artist?.memberIdx), collabs: resolve(artist?.collabIdx) }
  }, [index, artist])

  // Offline with nothing cached, the index store parks at 'idle' and never loads —
  // report "nothing to show" rather than a skeleton that never resolves.
  return { artist, upcoming, past, members, collabs, loading: loading && online }
}
